import React, { useState, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { Map, TileLayer } from 'components/Map';
import MapNotification from 'components/MapNotification';
import MapPageMeta from 'components/MapPageMeta/MapPageMeta';
import MapPlaceholder from 'components/MapPlaceholder';
import PageRail from 'components/PageRail';
import useMap from 'hooks/map/useMap';
import useMapLocation from 'hooks/map/useMapLocation';
import useSetupMapPage from 'hooks/map/useSetupMapPage';
import { useMapError, useMapLoading } from 'selectors/mapV2';
import type { MapMode } from 'types/map';
import cloneChildrenWithProps from 'utils/cloneChildrenWithProps';
import mapOptions from 'utils/mapOptions';

import { ATTRIBUTION, MAP_TILE_URL } from './constants';
import useResetMapStateOnUnmount from './useResetMapStateOnUnmount';
import useSyncLocationToMap from './useSyncLocationToMap';
import useSyncMapBoundingBoxToStore from './useSyncMapBoundingBoxToStore';
import useSyncMapLocationToStore from './useSyncMapLocationToStore';

import styles from './MapV2.module.scss';

const mapClassName = (loading: boolean, error: boolean) =>
  // TODO: Make V1 base class once we have migrated all map pages
  classNames('sl-map-page', 'sl-map-page-v2', styles.slMapPageV2, {
    [styles.slMapPageV2Loading]: loading,
    [styles.slMapPageV2Error]: error,
  });

const getLeftRailClass = () => classNames('sl-left-rail', styles.slMapV2LeftRail);

const MapV2 = ({
  children,
  mapMode = 'buoy',
}: {
  children: React.ReactElement;
  mapMode?: MapMode;
}) => {
  const [map, setMap] = useMap();
  const [leafletComponentItselfReady, setLeafletComponentItselfReady] = useState(false);
  const loading = useMapLoading();
  const error = useMapError();
  const mountedRef = useRef(true);
  const initializationTimerRef = useRef<number | null>(null);
  const mapInstanceRef = useRef<any>(null);

  // Synchronize map instance between ref and global state
  useEffect(() => {
    if (map) {
      mapInstanceRef.current = map;
    }
  }, [map]);

  // Reset map state when mapMode changes to ensure clean transitions
  useEffect(() => {
    // Clear the map state when mapMode changes to force re-initialization
    setMap(undefined);
    setLeafletComponentItselfReady(false);
    mapInstanceRef.current = null;
  }, [mapMode, setMap]);

  // Lifecycle management - separate effect to prevent premature cleanup
  useEffect(() => {
    // Explicitly set mounted to true on mount
    mountedRef.current = true;

    // Only clean up on unmount
    return () => {
      mountedRef.current = false;

      // Clean up any timers to avoid memory leaks
      if (initializationTimerRef.current !== null) {
        window.clearTimeout(initializationTimerRef.current);
        initializationTimerRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount

  // Simplified cleanup - let useMap hook handle map cleanup
  useEffect(
    () => () => {
      // Only run this cleanup when the component is truly unmounting
      if (!mountedRef.current) {
        // Clear the ref immediately
        mapInstanceRef.current = null;

        // Clear map state for this component - useMap will handle actual cleanup
        setMap(undefined);
      }
    },
    [setMap],
  );

  const handleLeafletComponentReady = useCallback(() => {
    // Only update state if component is still mounted
    if (mountedRef.current) {
      setLeafletComponentItselfReady(true);
    }
  }, []);

  const handleSetMapInstance = useCallback(
    (mapInstance: any) => {
      // Store reference to map instance immediately
      if (mapInstance) {
        mapInstanceRef.current = mapInstance;
      }

      // Only proceed if component is still mounted
      if (!mountedRef.current) {
        return;
      }

      if (mapInstance && !map) {
        try {
          // Set zoom control position immediately - don't delay this
          mapInstance.zoomControl.setPosition('topright');

          // Set map instance immediately for first load
          if (!map) {
            setMap(mapInstance);
          }

          // Add a small delay before invalidating size to ensure DOM is ready
          initializationTimerRef.current = window.setTimeout(() => {
            // Add extra safety checks before invalidating size
            if (mountedRef.current && mapInstance && mapInstanceRef.current === mapInstance) {
              try {
                // Check if the map container still exists in the DOM
                const container = mapInstance.getContainer();
                if (container && container.parentNode) {
                  mapInstance.invalidateSize(true); // Force Leaflet to recalculate sizes with animation
                }
              } catch (_err) {
                // Silently handle errors during resize
              }
            }
          }, 200); // 200ms delay to ensure DOM is ready
        } catch (_err) {
          // Silently handle errors during initialization
        }
      } else if (mapInstance && map) {
        // When map component is re-created due to mapMode key change, force update the global state
        if (mapInstance !== map) {
          try {
            mapInstance.zoomControl.setPosition('topright');
            setMap(mapInstance);
          } catch (_err) {
            // Silently handle errors during initialization
          }
        }

        // Still invalidate size to handle resize events with safety checks
        if (mountedRef.current && mapInstanceRef.current === mapInstance) {
          try {
            // Check if the map container still exists in the DOM
            const container = mapInstance.getContainer();
            if (container && container.parentNode) {
              mapInstance.invalidateSize(true);
            }
          } catch (_err) {
            // Silently handle errors during resize
          }
        }
      }
    },
    [map, setMap],
  );

  useSetupMapPage();
  useResetMapStateOnUnmount();

  // Order matters here, we want to make sure we sync the location to the map first since the other hooks depend
  // the location of the map
  useSyncLocationToMap();
  useSyncMapBoundingBoxToStore(map);
  useSyncMapLocationToStore();
  const { location } = useMapLocation();
  const childrenWithMap = cloneChildrenWithProps(children, { map });

  // Define mapProps here, so it's fresh when MapV2 re-renders due to leafletComponentItselfReady changing
  const currentMapProps = {
    ...mapOptions,
    onMapInstance: handleSetMapInstance,
    preferCanvas: false,
  };

  return (
    <div className={mapClassName(!!loading, !!error)}>
      {location && <MapPageMeta location={location} mapMode={mapMode} />}
      <PageRail side="right" className={styles.slMapV2RightRail}>
        {/* Show placeholder if the Leaflet component type isn't ready OR if the map instance isn't set yet */}
        {/* Map.js will render null if LeafletMap (component type) isn't ready. */}
        <MapPlaceholder show={!leafletComponentItselfReady || !map} />
        <Map
          key={`map-${mapMode}`} // Add key to force re-creation on mapMode change
          center={location.center}
          zoom={location.zoom}
          style={{}}
          onClick={() => {}}
          onZoomend={() => {}}
          onLeafletComponentReady={handleLeafletComponentReady}
          mapProps={currentMapProps}
        >
          <TileLayer attribution={ATTRIBUTION} url={MAP_TILE_URL} />
        </Map>
        {/* MapNotification should only appear if the map instance exists */}
        {map && <MapNotification hide={!map} mapMode={mapMode} />}
      </PageRail>
      <PageRail side="left" className={getLeftRailClass()}>
        {childrenWithMap}
      </PageRail>
    </div>
  );
};

export default MapV2;
